import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, ChevronDown } from "lucide-react";
import { WHATSAPP_DISPLAY } from "../lib/whatsapp";

const eventTypes = ["Mariage", "Vin d'honneur", "Conférence / Gala", "Réception privée", "Autre"];

const inputCls =
  "w-full border-b border-[#0a0a0a]/20 bg-transparent py-4 text-base text-[#0a0a0a] placeholder-[#0a0a0a]/30 outline-none transition-colors duration-300 focus:border-[#aa8c2c]";

export const QuoteForm = () => {
  const initial = { name: "", email: "", phone: "", type: eventTypes[0], serveurs: "", maitres: "1", heures: "", zone: "30km", date: "", place: "", guests: "", message: "" };
  const [form, setForm] = useState(initial);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const nb = (v) => parseInt(v, 10) || 0;
  const zoneFees = { "30km": 150, "50km": 250, europe: 0 };
  const zoneLabels = { "30km": "Paris / moins de 30 km", "50km": "30 à 50 km", europe: "Plus de 50 km / Europe" };
  const estimation = nb(form.heures) ? nb(form.serveurs) * 50 * nb(form.heures) + nb(form.maitres) * 70 * nb(form.heures) + (zoneFees[form.zone] || 0) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!expanded) {
      setExpanded(true);
      return;
    }
    setError(false);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/devis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!resp.ok) throw new Error();
      setSent(true);
    } catch {
      setError(true);
    }
  };

  const reset = () => {
    setForm(initial);
    setSent(false);
    setError(false);
    setExpanded(false);
  };

  return (
    <section id="devis" data-testid="quote-section" className="grain relative bg-[#f9f8f6] px-6 py-28 text-[#0a0a0a] lg:px-10 lg:py-40">
      <div className="mx-auto grid max-w-7xl gap-20 lg:grid-cols-[0.9fr_1.1fr]">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-6 text-[11px] font-semibold uppercase tracking-[0.4em] text-[#aa8c2c]">Devis</p>
          <h2 className="font-display text-4xl leading-tight tracking-tight text-[#0a0a0a] sm:text-5xl lg:text-6xl">
            Parlez-nous de <br />
            <em className="italic text-[#aa8c2c]">votre événement.</em>
          </h2>
          <p className="mt-8 max-w-sm text-sm leading-relaxed text-[#0a0a0a]/55">
            Remplissez ce formulaire : votre demande part immédiatement et vous recevez
            une confirmation par email et sur WhatsApp. Réponse rapide au{" "}
            <span className="font-semibold text-gold">{WHATSAPP_DISPLAY}</span>.
          </p>
          <img
            src="/images/maitre.png"
            alt="Maître d'hôtel FK supervisant une réception"
            data-testid="quote-image"
            className="mt-12 hidden aspect-[4/3] w-full max-w-md rounded-3xl border border-white/10 object-cover lg:block"
          />
        </motion.div>
        <motion.form
          onSubmit={submit}
          data-testid="quote-form"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="space-y-10 rounded-3xl border border-[#0a0a0a]/10 bg-white p-8 shadow-xl lg:p-12"
        >
          {sent ? (
            <div data-testid="quote-success" className="py-10 text-center">
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
              <h3 className="mt-6 font-display text-3xl tracking-tight text-[#0a0a0a]">
                Demande <em className="italic text-gold">bien reçue !</em>
              </h3>
              <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-[#0a0a0a]/55">
                Votre demande de devis a été envoyée. Vous recevrez une confirmation
                par email et sur WhatsApp sous 24 h.
              </p>
              <button
                type="button"
                onClick={reset}
                data-testid="quote-new-request"
                className="mt-8 rounded-full border border-gold/60 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
              >
                Nouvelle demande
              </button>
            </div>
          ) : (
          <>
          <div>
            <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Votre nom</label>
            <input
              type="text"
              value={form.name}
              onChange={set("name")}
              required
              placeholder="Ex. Marie N."
              data-testid="quote-input-name"
              onFocus={() => setExpanded(true)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Votre email</label>
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              required
              placeholder="vous@exemple.com"
              data-testid="quote-input-email"
              onFocus={() => setExpanded(true)}
              className={inputCls}
            />
          </div>
          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              data-testid="quote-expand-button"
              className="group flex w-full items-center justify-center gap-3 rounded-full border border-[#aa8c2c]/40 py-5 text-xs font-bold uppercase tracking-[0.25em] text-[#aa8c2c] transition-colors duration-300 hover:bg-[#aa8c2c] hover:text-white"
            >
              Continuer ma demande — date, lieu, équipe…
              <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          )}
          {expanded && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-10"
            data-testid="quote-extra-fields"
          >
          <div>
            <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Votre numéro WhatsApp</label>
            <input
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              required
              placeholder="06 12 34 56 78"
              data-testid="quote-input-phone"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Type d'événement</label>
            <select
              value={form.type}
              onChange={set("type")}
              data-testid="quote-select-type"
              className={`${inputCls} cursor-pointer bg-white`}
            >
              {eventTypes.map((t) => (
                <option key={t} value={t} className="bg-white text-black">{t}</option>
              ))}
            </select>
          </div>
          <div className="grid gap-10 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Date</label>
              <input
                type="text"
                value={form.date}
                onChange={set("date")}
                placeholder="Ex. 12 sept. 2026"
                data-testid="quote-input-date"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Lieu</label>
              <input
                type="text"
                value={form.place}
                onChange={set("place")}
                placeholder="Ville / salle"
                data-testid="quote-input-place"
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Nombre d'invités</label>
            <input
              type="text"
              value={form.guests}
              onChange={set("guests")}
              placeholder="Ex. 150"
              data-testid="quote-input-guests"
              className={inputCls}
            />
          </div>
          <div className="grid gap-10 sm:grid-cols-3">
            <div>
              <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Serveurs / serveuses</label>
              <input
                type="number"
                min="0"
                value={form.serveurs}
                onChange={set("serveurs")}
                required
                placeholder="Ex. 6"
                data-testid="quote-input-serveurs"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Maîtres d'hôtel</label>
              <input
                type="number"
                min="0"
                value={form.maitres}
                onChange={set("maitres")}
                required
                data-testid="quote-input-maitres"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Durée (heures)</label>
              <input
                type="number"
                min="1"
                value={form.heures}
                onChange={set("heures")}
                required
                placeholder="Ex. 8"
                data-testid="quote-input-heures"
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Zone de déplacement</label>
            <select
              value={form.zone}
              onChange={set("zone")}
              data-testid="quote-select-zone"
              className={`${inputCls} cursor-pointer bg-white`}
            >
              <option value="30km" className="bg-white text-black">Paris / moins de 30 km — forfait 150 €</option>
              <option value="50km" className="bg-white text-black">30 à 50 km — forfait 250 €</option>
              <option value="europe" className="bg-white text-black">Plus de 50 km / Europe — sur devis</option>
            </select>
          </div>
          {estimation > 0 && (
            <p data-testid="quote-estimation" className="rounded-2xl border border-[#aa8c2c]/30 bg-[#aa8c2c]/5 px-6 py-4 text-sm text-[#0a0a0a]/70">
              Estimation : <span className="font-display text-2xl text-[#aa8c2c]">{estimation.toLocaleString("fr-FR")} €</span>
              <span className="ml-2 text-xs text-[#0a0a0a]/45">{form.zone === "europe" ? "déplacement sur devis" : "déplacement inclus"}</span>
            </p>
          )}
          <div>
            <label className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0a0a0a]/45">Détails (optionnel)</label>
            <textarea
              value={form.message}
              onChange={set("message")}
              rows={3}
              placeholder="Nombre de serveurs souhaité, horaires, budget…"
              data-testid="quote-input-message"
              className={`${inputCls} resize-none`}
            />
          </div>
          <button
            type="submit"
            data-testid="quote-submit-button"
            className="group flex w-full items-center justify-center gap-3 rounded-full bg-gold py-5 text-xs font-bold uppercase tracking-[0.25em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.02]"
          >
            Envoyer ma demande de devis
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <p className="text-center text-xs text-[#0a0a0a]/45">
            Vous recevrez une confirmation par email et sur WhatsApp sous 24 h.
          </p>
          {error && (
            <p data-testid="quote-error" className="text-center text-xs text-red-400">
              Une erreur est survenue, merci de réessayer.
            </p>
          )}
          </motion.div>
          )}
          </>
          )}
        </motion.form>
      </div>
    </section>
  );
};
